/**
 * settingsModel — what the Practice Ops Settings panel edits, and exactly what it writes back.
 *
 * WHERE THE SETTINGS LIVE (verified 2026-10-30 by reading, not assumed): the Firestore board document
 * `practice-pulse/{siteID}`, field `settings` — and nowhere else. The exe (ppworker / event.js) only READS it
 * (an onSnapshot listener) and writes `ops` field-paths; the nightly reset (practice-pulse-service
 * `resetPracticePulse`) reads `settings` and writes the SAME object straight back into the rebuilt document;
 * the only SQL copy (`teamio.settings` PRACTICE_PULSE_SETTINGS) is written by a dead route. So any key we put under
 * `settings` survives the reset and the exe, and no second store needs the same write.
 *
 * WHAT THE PANEL OWNS. Only the keys listed in OWNED_KEYS are ever written, and only the ones the office actually
 * changed. Every other key under `settings` — the four old visual-alert toggles the classic Configure panel still shows and
 * Schedule View now owns (the tests name them: cypress/node-tests/practice-ops-settings.test.js DROPPED_TOGGLES), `ops`,
 * `quickNotesOptions`, `procCodesToWikiMappings` (dropped from this panel 2026-11-03; the classic Configure panel still edits it),
 * anything a later version adds — is neither shown nor sent, so it is never deleted: Firestore's merge
 * leaves it alone. That test and tests/component/OpsSettingsPanel.spec.js pin it.
 *
 * HOW A WRITE LOOKS. `settingsWritePayload(stored, values, DEL)` turns `{ <key>: <next value> }` into the object the
 * store merges (`setDoc(ref, { settings: payload }, { merge: true })`). Firestore's merge deep-merges nested maps, so
 * a key that DISAPPEARED from a map (a deleted quick-note category, a cleared room name) would silently survive;
 * `mergeWithDeletes` therefore sends `DEL` (Firestore's `deleteField()` — injected, so this file stays import-free) for
 * every map key that is in the stored value and not in the next one. Arrays and scalars simply replace. (The classic
 * Configure panel does the same through `updateOps`' `deletedSettingsKeys` — see utils/practiceOps/classicSettingsDeletes.js.)
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file.
 */

/** Every `settings` key the panel may write. Nothing outside this list is ever sent (so the four dropped toggles never are). */
export const OWNED_KEYS = [
  'arrivedStatus', 'readyStatus', 'dismissedStatus', 'inTreatmentRoomStatus',
  'elapsedTimeAlert', 'elapsedTime',
  'customAlerts', 'providerSettings', 'showChat',
  'quickNotesOptions2', 'sendEmailTime',
  'opsLayout', 'opLabels', 'allOps',
];

/** The four Open Dental statuses on the board, in the order the Statuses tab lists them. */
export const STATUS_ROWS = [
  { key: 'arrivedStatus', label: 'Arrived', desc: 'The patient is in the building. Starts the wait clock.' },
  { key: 'readyStatus', label: 'Ready', desc: 'Paperwork is done. Shown on the card, the clock keeps running.' },
  { key: 'inTreatmentRoomStatus', label: 'In treatment', desc: 'Written back to Open Dental when a card is dropped into an operatory.' },
  { key: 'dismissedStatus', label: 'Dismissed', desc: 'The patient has left. The card greys out in Check-out until the nightly reset.' },
];

/**
 * The post-op email hours — the classic time picker's own range (4 pm to 11 pm, hourly) written the way it writes them
 * ("hh:mm A": the service reads `moment(value, 'HH:mm a')`). The service emails the day's call list ONCE, at this hour, on the
 * day of the visit; "next morning" style times do not exist there, so they are not offered.
 */
export const SEND_TIME_OPTIONS = ['04:00 PM', '05:00 PM', '06:00 PM', '07:00 PM', '08:00 PM', '09:00 PM', '10:00 PM', '11:00 PM'];

/** The longest wait threshold the panel accepts (minutes). */
export const MAX_THRESHOLD_MIN = 480;

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const clone = (v) => (v === undefined ? undefined : JSON.parse(JSON.stringify(v)));
const str = (v) => (typeof v === 'string' ? v : '');
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function toPositiveInt(v) {
  if (typeof v === 'number') return Number.isInteger(v) && v > 0 ? v : null;
  if (typeof v === 'string' && /^\d+$/.test(v.trim())) {
    const n = parseInt(v.trim(), 10);
    return n > 0 ? n : null;
  }
  return null;
}

/**
 * "Show team chat on the Board" — ON unless the stored value is 'FALSE' (decision 2026-11-03: absent ⇒ ON, so an office that never
 * touched the setting and a brand-new site both get the chat; only an explicit 'FALSE' turns it off). The classic page reads it the same way.
 */
export function chatOn(settings) {
  const s = isObj(settings) ? settings : null;
  return !(s && s.showChat === 'FALSE');
}

// ── rooms ────────────────────────────────────────────────────────────────────────────────────────

/** The rooms the office has: settings.allOps (stored order), else the board document's own allOps. */
function listedRooms(stored, docAllOps) {
  const s = isObj(stored) ? stored : {};
  const list = Array.isArray(s.allOps) && s.allOps.length > 0 ? s.allOps : (Array.isArray(docAllOps) ? docAllOps : []);
  const seen = {};
  const out = [];
  list.forEach((op) => {
    if (!isObj(op)) return;
    const opNum = toPositiveInt(op.OperatoryNum);
    if (opNum === null || seen[opNum]) return;
    seen[opNum] = true;
    out.push({ opNum, odName: str(op.OpName).trim(), custom: op.addedFromTeamio === true });
  });
  return out;
}

/** `settings.opsLayout` as two clean id lists (the same reading board.js readOpsLayout does; agreement-tested). */
function layoutOf(stored) {
  const raw = isObj(stored) && isObj(stored.opsLayout) ? stored.opsLayout : null;
  const ids = (list) => {
    const seen = {};
    const out = [];
    (Array.isArray(list) ? list : []).forEach((v) => {
      const n = toPositiveInt(v);
      if (n === null || seen[n]) return;
      seen[n] = true;
      out.push(n);
    });
    return out;
  };
  return { order: ids(raw && raw.order), hidden: ids(raw && raw.hidden) };
}

/** The rows of the Operatories tab, in the order the board draws them (named rooms first, the rest after, in allOps order). */
export function roomRows(stored, docAllOps) {
  const rooms = listedRooms(stored, docAllOps);
  const layout = layoutOf(stored);
  const byNum = {};
  rooms.forEach((r) => { byNum[r.opNum] = r; });
  const first = layout.order.filter((n) => byNum[n]).map((n) => byNum[n]);
  const named = {};
  first.forEach((r) => { named[r.opNum] = true; });
  const ordered = first.concat(rooms.filter((r) => !named[r.opNum]));
  const hidden = {};
  layout.hidden.forEach((n) => { hidden[n] = true; });
  const labels = isObj(stored) && isObj(stored.opLabels) ? stored.opLabels : {};
  return ordered.map((r) => {
    const label = str(labels[r.opNum]).trim();
    return { opNum: r.opNum, odName: r.odName, name: label || r.odName, shown: !hidden[r.opNum], custom: r.custom };
  });
}

// ── the draft ────────────────────────────────────────────────────────────────────────────────────

const defNumOf = (status) => (isObj(status) && toPositiveInt(status.DefNum) !== null ? toPositiveInt(status.DefNum) : null);

/**
 * The editable copy of the stored settings: plain JSON, nothing shared with the store.
 * @param {?object} stored  the board document's `settings` (null / undefined before the first save)
 * @param {{docAllOps?: Array, employees?: Array}} ctx  the board document's allOps; the ACTIVE employees ({ EmployeeID, ... })
 */
export function buildDraft(stored, ctx) {
  const s = isObj(stored) ? stored : {};
  const c = isObj(ctx) ? ctx : {};
  const employees = Array.isArray(c.employees) ? c.employees : [];

  const statuses = {};
  STATUS_ROWS.forEach((row) => { statuses[row.key] = defNumOf(s[row.key]); });

  const minutes = Number(s.elapsedTime);
  const threshold = { on: s.elapsedTimeAlert === 'TRUE', minutes: Number.isFinite(minutes) && minutes > 0 ? minutes : '' };

  const quickAlerts = (Array.isArray(s.customAlerts) ? s.customAlerts : []).filter(isObj).map((a) => ({
    employeeID: isObj(a.employee) && typeof a.employee.EmployeeID === 'string' ? a.employee.EmployeeID : '',
    alertText: str(a.alertText),
  }));

  const providers = isObj(s.providerSettings) ? s.providerSettings : {};
  const team = {};
  employees.forEach((e) => {
    if (!isObj(e) || typeof e.EmployeeID !== 'string') return;
    const entry = isObj(providers[e.EmployeeID]) ? providers[e.EmployeeID] : {};
    team[e.EmployeeID] = {
      color: /^#[0-9a-f]{6}$/i.test(str(entry.color)) ? entry.color : '',
      sound: isObj(entry.sound) ? str(entry.sound.name) : '',
      email: str(entry.email),
    };
  });

  const notes = isObj(s.quickNotesOptions2) ? s.quickNotesOptions2 : {};
  const categories = Object.keys(notes).filter((k) => isObj(notes[k])).map((key) => ({
    key,
    title: str(notes[key].title),
    items: (Array.isArray(notes[key].val) ? notes[key].val : []).filter(isObj).map((it) => ({ name: str(it.name), autoNotes: str(it.autoNotes) })),
  }));

  return {
    statuses,
    threshold,
    quickAlerts,
    team,
    chatOn: chatOn(s),
    categories,
    sendEmailTime: str(s.sendEmailTime),
    rooms: roomRows(s, c.docAllOps),
  };
}

// ── validation ───────────────────────────────────────────────────────────────────────────────────

/**
 * What stops a save: `[{ tab, message }]`, empty when the draft is fine.
 * @param {object} draft
 * @param {{firstTime?: boolean}} [opts]  firstTime = there are no stored settings yet: all four statuses must be chosen
 */
export function validateDraft(draft, opts) {
  const errors = [];
  const d = isObj(draft) ? draft : {};
  const firstTime = !!(opts && opts.firstTime);

  if (firstTime) {
    STATUS_ROWS.forEach((row) => {
      if (!d.statuses || d.statuses[row.key] === null || d.statuses[row.key] === undefined) {
        errors.push({ tab: 'statuses', message: `Choose the Open Dental status for "${row.label}".` });
      }
    });
  }

  if (d.threshold && d.threshold.on) {
    const n = Number(d.threshold.minutes);
    if (d.threshold.minutes === '' || !Number.isInteger(n) || n < 1 || n > MAX_THRESHOLD_MIN) {
      errors.push({ tab: 'alerts', message: `Enter the minutes before a wait is flagged (a whole number from 1 to ${MAX_THRESHOLD_MIN}).` });
    }
  }

  (Array.isArray(d.quickAlerts) ? d.quickAlerts : []).forEach((a) => {
    if (str(a.alertText).trim() !== '' && !a.employeeID) {
      errors.push({ tab: 'alerts', message: `Pick a team member for the quick alert "${str(a.alertText).trim()}".` });
    }
  });

  Object.keys(isObj(d.team) ? d.team : {}).forEach((id) => {
    const email = str(d.team[id].email).trim();
    if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push({ tab: 'checkout', message: `"${email}" is not an email address.` });
    }
  });

  return errors;
}

// ── the changes ──────────────────────────────────────────────────────────────────────────────────

function statusObject(defNum, stored, list) {
  if (isObj(stored) && defNumOf(stored) === defNum) return clone(stored);
  const found = (Array.isArray(list) ? list : []).find((s) => isObj(s) && toPositiveInt(s.DefNum) === defNum);
  return found ? clone(found) : null;
}

/**
 * Turn the edited draft into `{ values, sections }`: `values` = { <settings key>: <next value> } for ONLY what changed
 * (compared with `baseline`, the draft as it was when the panel opened); `sections` = which parts changed.
 *
 * @param {?object} stored    the board document's settings (never mutated)
 * @param {object} draft      the edited draft
 * @param {object} baseline   buildDraft(stored, ctx) taken when the panel opened
 * @param {{docAllOps?: Array, employees?: Array, statuses?: Array, sounds?: Array}} ctx
 *        statuses = the office's Open Dental statuses ([{ DefNum, ItemName, ... }]); sounds = alertSounds.js list
 */
export function buildChanges(stored, draft, baseline, ctx) {
  const s = isObj(stored) ? stored : {};
  const c = isObj(ctx) ? ctx : {};
  const d = isObj(draft) ? draft : {};
  const b = isObj(baseline) ? baseline : {};
  const values = {};
  const sections = [];
  const mark = (name) => { if (sections.indexOf(name) === -1) sections.push(name); };

  // First write ever (no stored allOps): seed it, as the classic panel does — the nightly reset rebuilds the board from it.
  if (!(Array.isArray(s.allOps) && s.allOps.length > 0) && Array.isArray(c.docAllOps) && c.docAllOps.length > 0) {
    values.allOps = clone(c.docAllOps);
  }

  // Statuses: one key per status the office changed. The whole status object is stored (the exe reads `.DefNum`).
  STATUS_ROWS.forEach((row) => {
    const next = d.statuses ? d.statuses[row.key] : null;
    const was = b.statuses ? b.statuses[row.key] : null;
    if (next === was || next === null || next === undefined) return;
    const obj = statusObject(next, s[row.key], c.statuses);
    if (obj) { values[row.key] = obj; mark('statuses'); }
  });

  if (!same(d.threshold, b.threshold) && isObj(d.threshold)) {
    values.elapsedTimeAlert = d.threshold.on ? 'TRUE' : 'FALSE';
    if (d.threshold.on) values.elapsedTime = Number(d.threshold.minutes);
    mark('alerts');
  }

  if (!same(d.quickAlerts, b.quickAlerts)) {
    const storedAlerts = Array.isArray(s.customAlerts) ? s.customAlerts : [];
    const employees = Array.isArray(c.employees) ? c.employees : [];
    values.customAlerts = (Array.isArray(d.quickAlerts) ? d.quickAlerts : [])
      .map((a, i) => ({ a, old: isObj(storedAlerts[i]) ? storedAlerts[i] : null }))
      .filter(({ a }) => str(a.alertText).trim() !== '')
      .map(({ a, old }) => {
        const emp = employees.find((e) => isObj(e) && e.EmployeeID === a.employeeID)
          || (old && isObj(old.employee) && old.employee.EmployeeID === a.employeeID ? old.employee : null);
        return Object.assign({}, old ? clone(old) : {}, { employee: emp ? clone(emp) : null, alertText: str(a.alertText).trim() });
      });
    mark('alerts');
  }

  // Per team member: colour + sound (Alerts tab) and email (Check-out tab) share ONE map, providerSettings.
  const teamNow = isObj(d.team) ? d.team : {};
  const teamWas = isObj(b.team) ? b.team : {};
  const changedIds = Object.keys(teamNow).filter((id) => !same(teamNow[id], teamWas[id]));
  if (changedIds.length) {
    const providers = isObj(s.providerSettings) ? s.providerSettings : {};
    const next = clone(providers) || {};
    const employees = Array.isArray(c.employees) ? c.employees : [];
    const sounds = Array.isArray(c.sounds) ? c.sounds : [];
    changedIds.forEach((id) => {
      const row = teamNow[id];
      const was = teamWas[id] || {};
      const entry = isObj(next[id]) ? next[id] : {};
      if (!isObj(entry.employee)) {
        const emp = employees.find((e) => isObj(e) && e.EmployeeID === id);
        if (emp) entry.employee = clone(emp);
      }
      entry.color = row.color;
      if (row.email !== was.email) entry.email = str(row.email).trim();
      if (row.sound !== was.sound) {
        const picked = sounds.find((x) => x && x.name === row.sound);
        if (picked) entry.sound = clone(picked);
        else entry.sound = null;       // "No sound": null, which the page's ping watcher treats as no sound (an empty {} would play the default)
      }
      next[id] = entry;
      if (row.color !== was.color || row.sound !== was.sound) mark('alerts');
      if (row.email !== was.email) mark('checkout');
    });
    values.providerSettings = next;
  }

  if (d.chatOn !== b.chatOn) {
    values.showChat = d.chatOn ? 'TRUE' : 'FALSE';
    mark('alerts');
  }

  if (!same(d.categories, b.categories)) {
    const next = {};
    (Array.isArray(d.categories) ? d.categories : []).forEach((cat, i) => {
      const items = (Array.isArray(cat.items) ? cat.items : [])
        .filter((it) => str(it.name).trim() !== '')
        .map((it) => ({ name: str(it.name).trim(), checked: false, autoNotes: str(it.autoNotes) }));
      const title = str(cat.title).trim();
      if (title === '' && items.length === 0) return;
      // New categories get a time-based key (the classic panel's own scheme); sorted keys keep them in order.
      const key = cat.key ? String(cat.key) : String(Date.now() + i);
      next[key] = { title, val: items };
    });
    values.quickNotesOptions2 = next;
    mark('checkout');
  }

  if (d.sendEmailTime !== b.sendEmailTime) {
    values.sendEmailTime = str(d.sendEmailTime);
    mark('checkout');
  }

  if (!same(d.rooms, b.rooms)) {
    const rooms = Array.isArray(d.rooms) ? d.rooms : [];
    const was = Array.isArray(b.rooms) ? b.rooms : [];
    const orderNow = rooms.map((r) => r.opNum);
    const hiddenNow = rooms.filter((r) => !r.shown).map((r) => r.opNum);
    const layoutChanged = !same(orderNow, was.map((r) => r.opNum)) || !same(hiddenNow, was.filter((r) => !r.shown).map((r) => r.opNum));
    if (layoutChanged) values.opsLayout = { order: orderNow, hidden: hiddenNow };

    // Renames: start from the stored labels (ids that are not rooms stay), then set / clear the rows that changed.
    const labels = isObj(s.opLabels) ? clone(s.opLabels) : {};
    let labelsChanged = false;
    rooms.forEach((r) => {
      const before = was.find((w) => w.opNum === r.opNum);
      const name = str(r.name).trim();
      if (before && str(before.name).trim() === name) return;
      labelsChanged = true;
      if (name === '' || name === r.odName) delete labels[r.opNum];
      else labels[r.opNum] = name;
    });
    if (labelsChanged) values.opLabels = labels;
    if (layoutChanged || labelsChanged) mark('rooms');
  }

  return { values, sections };
}

// ── the write ────────────────────────────────────────────────────────────────────────────────────

/**
 * `next` as a merge payload against `stored`: plain objects are walked (a key that is in `stored` and not in `next` becomes
 * `DEL`); arrays, scalars and null replace. Never mutates either argument.
 */
export function mergeWithDeletes(stored, next, DEL) {
  if (!isObj(next)) return next;
  const out = {};
  const old = isObj(stored) ? stored : {};
  Object.keys(next).forEach((k) => {
    if (next[k] === undefined) return;
    out[k] = mergeWithDeletes(old[k], next[k], DEL);
  });
  Object.keys(old).forEach((k) => {
    if (!Object.prototype.hasOwnProperty.call(next, k)) out[k] = DEL;
  });
  return out;
}

/**
 * The object to store under `settings` for `values` ({ <owned key>: <next value> }). A key outside OWNED_KEYS is refused
 * (never sent) — this is the one place that enforces "the panel writes only what it owns".
 */
export function settingsWritePayload(stored, values, DEL) {
  const out = {};
  const old = isObj(stored) ? stored : {};
  Object.keys(isObj(values) ? values : {}).forEach((k) => {
    if (OWNED_KEYS.indexOf(k) === -1) return;
    out[k] = mergeWithDeletes(old[k], values[k], DEL);
  });
  return out;
}
